import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Quote } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Quotes — Business, Money & Motivation";
const DESC =
  "The best Raj Shamani quotes on business, money, discipline and building in public — pulled from Build Don't Talk and the Figuring Out podcast, with context for each line.";
const URL = "https://rajshamani.life/raj-shamani-quotes";
const OG = OG_ABOUT;

const quotes: Array<{ text: string; context: string }> = [
  {
    text: "Build first. Talk later. The work is the loudest thing you own.",
    context:
      "The central idea of Build, Don't Talk — credibility is earned by shipping, not by announcing.",
  },
  {
    text: "You don't need permission to start. You need a first customer.",
    context:
      "A line he repeats with first-time founders on Figuring Out — validation beats planning.",
  },
  {
    text: "Money is a skill, not a secret. Skills can be learned.",
    context:
      "From his money episodes, arguing that financial literacy is a practised habit, not luck.",
  },
  {
    text: "Distribution is the business. The product is just the excuse to have one.",
    context:
      "His most-quoted D2C line — how Indian consumer brands actually scale.",
  },
  {
    text: "Consistency is a compounding asset. Most people sell it too early.",
    context:
      "On publishing daily content for years before the podcast found an audience.",
  },
  {
    text: "Your network is not who you know. It's who takes your call twice.",
    context: "On building real relationships instead of collecting contacts.",
  },
  {
    text: "Failure is only expensive when you refuse to write it down.",
    context: "The thinking behind his public failure resume.",
  },
  {
    text: "Curiosity scales better than confidence.",
    context:
      "How he approaches interviews — asking the obvious question everyone is too embarrassed to ask.",
  },
];

const faq = [
  {
    q: "What is Raj Shamani's most famous quote?",
    a: "His most repeated line is \"Build first. Talk later.\" — the core argument of his book Build, Don't Talk, which says credibility comes from shipping work rather than announcing plans.",
  },
  {
    q: "Where do Raj Shamani's quotes come from?",
    a: "Most widely-shared Raj Shamani quotes come from three places: his book Build, Don't Talk (2022), episodes of the Figuring Out podcast, and his daily posts on Instagram and LinkedIn.",
  },
  {
    q: "What does Raj Shamani say about money?",
    a: "He frames money as a learnable skill rather than a secret — arguing that income, savings and investing are habits that can be practised, and that most people delay starting because they wait to feel ready.",
  },
  {
    q: "What does 'Build, Don't Talk' actually mean?",
    a: "It means doing the work before broadcasting it. Raj Shamani argues that visibility earned from finished work compounds, while visibility from announcements creates pressure without proof.",
  },
];

export const Route = createFileRoute("/raj-shamani-quotes")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani quotes", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: QuotesPage,
});

function QuotesPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Quotes" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Quote size={14} /> In his own words
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">quotes</span> on business, money and building
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            The lines that get screenshotted most often — collected from{" "}
            <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">
              Build, Don&apos;t Talk
            </Link>
            , the{" "}
            <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
              Figuring Out podcast
            </Link>{" "}
            and his daily posts, each with the context that makes them useful rather than just
            quotable.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Best Raj Shamani quotes</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {quotes.map((q) => (
            <figure key={q.text} className="rounded-xl border border-border bg-card p-6">
              <blockquote className="text-lg font-medium leading-relaxed">
                &ldquo;{q.text}&rdquo;
              </blockquote>
              <figcaption className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {q.context}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Why these ideas repeat</h2>
        <div className="space-y-5 text-muted-foreground leading-relaxed max-w-3xl">
          <p>
            Raj Shamani&apos;s quotes cluster around three themes, and they repeat because they come
            from the same lived sequence: a manufacturing business at 16, years of unpaid publishing,
            then a podcast and a book that only worked because the work existed first.
          </p>
          <p>
            The first theme is <strong className="text-foreground">proof over promise</strong>. Build,
            Don&apos;t Talk is an argument against announcing intentions, because announcements borrow
            credibility you have not yet earned.
          </p>
          <p>
            The second is <strong className="text-foreground">money as a skill</strong>. He treats
            earning, saving and investing as practised behaviours rather than personality traits — the
            reason his money episodes lean practical rather than motivational.
          </p>
          <p>
            The third is <strong className="text-foreground">distribution</strong>. Having built a{" "}
            <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
              manufacturing and distribution business
            </Link>{" "}
            before becoming a creator, he consistently argues that reaching customers is the harder
            half of any consumer business.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-quotes" />

      <RelatedPages pages={["aion", "book", "podcast", "about", "faq", "ventures"]} />
    </div>
  );
}
